import React from 'react';
import { Play, Youtube, Music2, Cloud } from 'lucide-react';

interface UniversalEmbedPlayerProps {
  platform: string;
  externalId: string;
  className?: string;
}

export const UniversalEmbedPlayer: React.FC<UniversalEmbedPlayerProps> = ({ 
  platform, 
  externalId, 
  className = "" 
}) => {
  const p = platform.toLowerCase();

  // YouTube
  if (p === 'youtube') {
    return (
      <div className={`aspect-video w-full overflow-hidden rounded-xl bg-black ${className}`}>
        <iframe
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${externalId}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      </div>
    );
  }

  // TikTok
  if (p === 'tiktok') {
    // For TikTok, we use their official embed URL structure
    // Note: externalId for TikTok is usually the video_id or the full URL
    const videoId = externalId.includes('/') ? externalId.split('/').pop() : externalId;
    
    return (
      <div className={`aspect-[9/16] max-h-[600px] mx-auto w-full overflow-hidden rounded-xl bg-black flex items-center justify-center ${className}`}>
        <iframe
          src={`https://www.tiktok.com/embed/v2/${videoId}`}
          style={{ width: '100%', height: '100%', border: 'none' }}
          allowFullScreen
          allow="autoplay; encrypted-media"
        ></iframe>
      </div>
    );
  }

  // Bluesky
  if (p === 'bluesky') {
    // Bluesky embeds usually need their SDK or a specific embed URL
    // For simplicity, we can link to it or use an iframe if they provide one
    return (
      <div className={`aspect-video w-full overflow-hidden rounded-xl bg-muted border border-border flex flex-col items-center justify-center gap-4 p-6 ${className}`}>
        <Cloud className="h-12 w-12 text-primary animate-pulse" />
        <div className="text-center">
          <p className="text-foreground font-bold">Post on Bluesky</p>
          <p className="text-muted-foreground text-xs mt-1">Video is hosted on Bluesky</p>
        </div>
        <a 
          href={externalId.startsWith('at://') ? `https://bsky.app/profile/${externalId.split('/')[2]}/post/${externalId.split('/').pop()}` : externalId}
          target="_blank"
          rel="noopener noreferrer"
          className="px-6 py-2 bg-primary hover:bg-primary/90 text-white rounded-full font-bold text-sm transition-all"
        >
          View on Bluesky
        </a>
      </div>
    );
  }

  // Fallback for other platforms
  return (
    <div className={`aspect-video w-full overflow-hidden rounded-xl bg-muted border border-border flex flex-col items-center justify-center gap-3 ${className}`}>
      <Play className="h-10 w-10 text-muted-foreground" />
      <p className="text-muted-foreground text-xs font-medium uppercase tracking-widest">Media Externalized</p>
    </div>
  );
};

export default UniversalEmbedPlayer;
